import { Button, Input, Select } from "@arco-design/web-react";
import { RotateCcw, Search } from "lucide-react";
import { useState } from "react";

import { CrmListFilterMoreButton } from "../../../shared/ui/CrmListFilterMoreButton";
import {
  supplierBasicStatusOptions,
  supplierCooperationStatusOptions,
  supplierEligibilityOptions,
  supplierSubjectNatureOptions,
} from "../config";
import type { SupplierListFilters } from "../types";
import "../../../shared/ui/crmListPageLayout.css";
import "./SupplyBasicPage.css";

const FILTER_ALL = "__all__";

type SupplyBasicFilterPanelProps = {
  title: string;
  filters: SupplierListFilters;
  errorMessage: string;
  onChange: (filters: SupplierListFilters) => void;
  onReset: () => void;
  onSearch: () => void;
};

/** 供应商基础设置筛选区。 */
export function SupplyBasicFilterPanel({
  title,
  filters,
  errorMessage,
  onChange,
  onReset,
  onSearch,
}: SupplyBasicFilterPanelProps) {
  const [advancedFiltersVisible, setAdvancedFiltersVisible] = useState(false);

  return (
    <section
      className="crm-list-filter-panel"
      role="search"
      aria-label={`${title}筛选`}
      data-layout="two-tier-responsive"
    >
      <div className="crm-list-filter-fields">
        <div className="crm-list-filter-primary">
          <label className="crm-list-field crm-list-field--lg">
            <span>关键词</span>
            <Input
              placeholder={`${title}名称、编号或主体`}
              value={filters.q ?? ""}
              onChange={(q) => onChange({ ...filters, q })}
            />
          </label>
          <div className="crm-list-field crm-list-field--md">
            <span>合作状态</span>
            <Select
              placeholder="全部合作状态"
              triggerProps={{ autoFitPosition: true, position: "bl" }}
              getPopupContainer={() => document.body}
              value={filters.cooperationStatus ?? FILTER_ALL}
              onChange={(value) =>
                onChange({
                  ...filters,
                  cooperationStatus: parseOptionalFilterValue(
                    value,
                  ) as SupplierListFilters["cooperationStatus"],
                })
              }
            >
              {supplierCooperationStatusOptions.map((option) => (
                <Select.Option
                  key={option.value || FILTER_ALL}
                  value={option.value || FILTER_ALL}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="crm-list-field crm-list-field--sm">
            <span>状态</span>
            <Select
              aria-label="状态"
              placeholder="全部状态"
              triggerProps={{ autoFitPosition: true, position: "bl" }}
              getPopupContainer={() => document.body}
              value={
                filters.isDisabled === undefined ? FILTER_ALL : String(filters.isDisabled)
              }
              onChange={(value) =>
                onChange({
                  ...filters,
                  isDisabled: parseBooleanFilterValue(value),
                })
              }
            >
              {supplierBasicStatusOptions.map((option) => (
                <Select.Option
                  key={option.value || FILTER_ALL}
                  value={option.value || FILTER_ALL}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>

        {advancedFiltersVisible ? (
          <div className="crm-list-filter-advanced">
            <div className="crm-list-field crm-list-field--md">
              <span>主体性质</span>
              <Select
                placeholder="全部主体性质"
                triggerProps={{ autoFitPosition: true, position: "bl" }}
                getPopupContainer={() => document.body}
                value={filters.subjectNature ?? FILTER_ALL}
                onChange={(value) =>
                  onChange({
                    ...filters,
                    subjectNature: parseOptionalFilterValue(
                      value,
                    ) as SupplierListFilters["subjectNature"],
                  })
                }
              >
                {supplierSubjectNatureOptions.map((option) => (
                  <Select.Option
                    key={option.value || FILTER_ALL}
                    value={option.value || FILTER_ALL}
                  >
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <EligibilityFilter
              label="履约资格"
              value={filters.canBeFulfillmentSubject}
              onChange={(canBeFulfillmentSubject) =>
                onChange({ ...filters, canBeFulfillmentSubject })
              }
            />
            <EligibilityFilter
              label="结算资格"
              value={filters.canBeSettlementSubject}
              onChange={(canBeSettlementSubject) =>
                onChange({ ...filters, canBeSettlementSubject })
              }
            />
            <EligibilityFilter
              label="报价资格"
              value={filters.canBeQuoteSubject}
              onChange={(canBeQuoteSubject) => onChange({ ...filters, canBeQuoteSubject })}
            />
            <label className="crm-list-field crm-list-field--md">
              <span>省份</span>
              <Input
                placeholder="请输入省份"
                value={filters.province ?? ""}
                onChange={(province) => onChange({ ...filters, province })}
              />
            </label>
            <label className="crm-list-field crm-list-field--md">
              <span>城市</span>
              <Input
                placeholder="请输入城市"
                value={filters.city ?? ""}
                onChange={(city) => onChange({ ...filters, city })}
              />
            </label>
          </div>
        ) : null}

        {errorMessage ? <div className="supply-basic-error">{errorMessage}</div> : null}
      </div>
      <div className="crm-list-filter-actions">
        <CrmListFilterMoreButton
          expanded={advancedFiltersVisible}
          onToggle={() => setAdvancedFiltersVisible((current) => !current)}
        />
        <Button icon={<RotateCcw size={16} />} onClick={onReset}>
          重置
        </Button>
        <Button type="primary" icon={<Search size={16} />} onClick={onSearch}>
          搜索
        </Button>
      </div>
    </section>
  );
}

/** Tri-state eligibility filter: all, qualified, or not qualified. */
function EligibilityFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: boolean;
  onChange: (value?: boolean) => void;
}) {
  return (
    <div className="crm-list-field crm-list-field--md">
      <span>{label}</span>
      <Select
        placeholder="全部"
        triggerProps={{ autoFitPosition: true, position: "bl" }}
        getPopupContainer={() => document.body}
        value={value === undefined ? FILTER_ALL : String(value)}
        onChange={(nextValue) => onChange(parseBooleanFilterValue(nextValue))}
      >
        {supplierEligibilityOptions.map((option) => (
          <Select.Option key={option.value || FILTER_ALL} value={option.value || FILTER_ALL}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
}

/** 把“全部”哨兵值映射为未选中的筛选参数。 */
function parseOptionalFilterValue(value?: string) {
  if (!value || value === FILTER_ALL) {
    return undefined;
  }
  return value;
}

/** 把布尔筛选下拉值映射为 true/false/undefined。 */
function parseBooleanFilterValue(value?: string) {
  if (!value || value === FILTER_ALL) {
    return undefined;
  }
  return value === "true";
}
